import apiClient from './axios';
import type { ApiEnvelope } from '../types';

export type OrderStatus =
  | 'placed'
  | 'processing'
  | 'shipped'
  | 'out_for_delivery'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'disputed';

export interface OrderItem {
  productId: string;
  productName: string;
  productImageUrl: string | null;
  qty: number;
  unitPrice: number;
}

export interface OrderStatusHistoryEntry {
  status: OrderStatus;
  at: string;
  note?: string;
}

export interface ShippingAddress {
  fullName: string;
  phone: string;
  addressLine: string;
  city: string;
  state: string;
  country: string;
  notes?: string;
}

export interface VendorOrder {
  id: string;
  reference: string;
  groupId: string | null;
  userId: string;
  vendorId: string;
  status: OrderStatus;
  subtotal: number;
  shipping: number;
  total: number;
  shippingAddress: ShippingAddress;
  statusHistory: OrderStatusHistoryEntry[];
  deliveredAt: string | null;
  confirmedReceivedAt: string | null;
  escrowReleasedAt: string | null;
  items: OrderItem[];
  placedAt: string;
}

export interface OrderListResponse {
  items: VendorOrder[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface OrderStats {
  total: number;
  /** Orders awaiting vendor action (placed + processing). */
  pending: number;
  byStatus: Record<OrderStatus, number>;
}

/** Statuses a VENDOR can move an order INTO (must match backend's
 *  vendor-orders.service VENDOR_ALLOWED_NEXT). */
export const VENDOR_NEXT_STATUS: Record<OrderStatus, OrderStatus[]> = {
  placed: ['processing'],
  processing: ['shipped'],
  shipped: ['out_for_delivery'],
  out_for_delivery: ['delivered'],
  delivered: [],
  completed: [],
  cancelled: [],
  disputed: [],
};

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  placed: 'Placed',
  processing: 'Processing',
  shipped: 'Shipped',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  disputed: 'Disputed',
};

export const ordersApi = {
  list: async (opts: {
    status?: OrderStatus;
    page?: number;
    limit?: number;
  } = {}): Promise<OrderListResponse> => {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const { data } = await apiClient.get<ApiEnvelope<OrderListResponse>>(
      `/vendor/orders${params.toString() ? `?${params.toString()}` : ''}`,
    );
    return data.data;
  },

  stats: async (): Promise<OrderStats> => {
    const { data } = await apiClient.get<ApiEnvelope<OrderStats>>(
      '/vendor/orders/stats',
    );
    return data.data;
  },

  getOne: async (id: string): Promise<VendorOrder> => {
    const { data } = await apiClient.get<ApiEnvelope<VendorOrder>>(
      `/vendor/orders/${id}`,
    );
    return data.data;
  },

  advance: async (
    id: string,
    nextStatus: OrderStatus,
    note?: string,
  ): Promise<VendorOrder> => {
    const { data } = await apiClient.post<ApiEnvelope<VendorOrder>>(
      `/vendor/orders/${id}/advance`,
      { nextStatus, note },
    );
    return data.data;
  },

  cancel: async (id: string, reason?: string): Promise<VendorOrder> => {
    const { data } = await apiClient.post<ApiEnvelope<VendorOrder>>(
      `/vendor/orders/${id}/cancel`,
      { reason },
    );
    return data.data;
  },
};
